import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hub } from 'aws-amplify/utils';
import { FaGoogle, FaApple } from 'react-icons/fa';
import { socialSignIn } from '../../auth';
import { useAuth } from '../../context/AuthContext';

export default function AppLogin() {
  const navigate = useNavigate();
  const { authUser, loading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState<'Google' | 'Apple' | null>(null);

  useEffect(() => {
    if (!loading && authUser) {
      navigate('/groups', { replace: true });
    }
  }, [authUser, loading, navigate]);

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        navigate('/groups', { replace: true });
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleSocialSignIn = async (provider: 'Google' | 'Apple') => {
    setSigningIn(provider);
    setError(null);
    try {
      await socialSignIn(provider);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign in failed. Please try again.');
      setSigningIn(null);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(145deg, #fff8f2 0%, #f0f4ff 60%, #f7fff4 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        fontFamily: 'inherit',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '420px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Card */}
        <div
          style={{
            width: '100%',
            backgroundColor: '#ffffff',
            borderRadius: '24px',
            padding: '2.5rem 2.25rem',
            boxShadow: '0 8px 48px rgba(0,0,0,0.1)',
            border: '1px solid #eef0f6',
          }}
        >
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: '2.25rem' }}>
            <div
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                backgroundColor: '#fff5eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                border: '2px solid #ffe0c2',
              }}
            >
              <img src="/sahayy-icon-nobg.png" alt="Sahayy" style={{ width: '52px', height: '52px', objectFit: 'contain' }} />
            </div>
            <h1
              style={{
                color: '#1a1f36',
                fontWeight: 800,
                fontSize: '1.625rem',
                margin: '0 0 0.35rem',
                letterSpacing: '-0.03em',
              }}
            >
              Welcome to Sahayy
            </h1>
            <p style={{ color: '#8896ab', fontSize: '0.9rem', margin: 0, lineHeight: 1.5 }}>
              Connect with your local community
            </p>
          </div>

          {/* Error */}
          {error && (
            <div
              style={{
                backgroundColor: '#fff5f5',
                border: '1px solid #fed7d7',
                borderRadius: '10px',
                padding: '0.75rem 1rem',
                marginBottom: '1rem',
                color: '#c53030',
                fontSize: '0.85rem',
                textAlign: 'center',
              }}
            >
              {error}
            </div>
          )}

          {/* Google Button */}
          <button
            onClick={() => handleSocialSignIn('Google')}
            disabled={signingIn !== null}
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              backgroundColor: '#ffffff',
              border: '1.5px solid #e2e6f0',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              cursor: signingIn !== null ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#1a1f36',
              marginBottom: '0.75rem',
              opacity: signingIn === 'Apple' ? 0.5 : 1,
              transition: 'all 0.15s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}
            onMouseEnter={(e) => {
              if (signingIn === null) {
                e.currentTarget.style.borderColor = '#c5cce0';
                e.currentTarget.style.boxShadow = '0 3px 12px rgba(0,0,0,0.1)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e2e6f0';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
            }}
          >
            <FaGoogle style={{ color: '#4285f4', fontSize: '1.1rem', flexShrink: 0 }} />
            {signingIn === 'Google' ? 'Redirecting to Google...' : 'Continue with Google'}
          </button>

          {/* Apple Button */}
          <button
            onClick={() => handleSocialSignIn('Apple')}
            disabled={signingIn !== null}
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              backgroundColor: '#1a1a1a',
              border: '1.5px solid #1a1a1a',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              cursor: signingIn !== null ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#ffffff',
              marginBottom: '1.75rem',
              opacity: signingIn === 'Google' ? 0.5 : 1,
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              if (signingIn === null) e.currentTarget.style.backgroundColor = '#000000';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#1a1a1a';
            }}
          >
            <FaApple style={{ fontSize: '1.2rem', flexShrink: 0 }} />
            {signingIn === 'Apple' ? 'Redirecting to Apple...' : 'Continue with Apple'}
          </button>

          {/* Terms */}
          <p
            style={{
              color: '#8896ab',
              fontSize: '0.75rem',
              textAlign: 'center',
              margin: 0,
              lineHeight: 1.6,
            }}
          >
            By signing in you agree to our{' '}
            <a href="/terms-of-service" style={{ color: '#FF385C', textDecoration: 'none', fontWeight: 500 }}>
              Terms
            </a>{' '}
            &{' '}
            <a href="/privacy-policy" style={{ color: '#FF385C', textDecoration: 'none', fontWeight: 500 }}>
              Privacy Policy
            </a>
          </p>
        </div>

        {/* Quote */}
        <p
          style={{
            color: '#8896ab',
            fontSize: '0.8rem',
            textAlign: 'center',
            marginTop: '1.75rem',
            fontStyle: 'italic',
            lineHeight: 1.7,
            padding: '0 1rem',
          }}
        >
          "एकमेका सहाय्य करू अवघे धरू सुपंथ!!"
          <br />
          <span style={{ fontSize: '0.72rem', opacity: 0.7 }}>
            Inspired by Sant Tukaram Maharaj's timeless wisdom
          </span>
        </p>
      </div>
    </div>
  );
}
